import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Users, FolderKanban, Plus, List, Settings, UserCheck } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';
import { toast } from 'sonner';
import ConfirmationModal from '@/components/ConfirmationModal';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    activeProjects: 0
  });
  const [loading, setLoading] = useState(true);
  const [playgroundActive, setPlaygroundActive] = useState(false);
  const [updatingPlayground, setUpdatingPlayground] = useState(false);
  const [showResetPlaygroundModal, setShowResetPlaygroundModal] = useState(false);
  const [newUsersCount, setNewUsersCount] = useState(0);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  useEffect(() => {
    loadStats();
    loadPlaygroundSettings();
    loadNewUsersCount();
    loadPendingApprovalCount();
  }, []);

  const loadPendingApprovalCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-approval/count`);
      setPendingApprovalCount(res.data.count || 0);
    } catch (err) { /* ignore */ }
  };

  const loadNewUsersCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/new-users/count`);
      setNewUsersCount(res.data.count || 0);
    } catch (err) { /* ignore */ }
  };

  const loadStats = async () => {
    try {
      // Use admin stats endpoint for accurate counts
      const statsRes = await axios.get(`${API}/admin/stats`);
      
      setStats({
        totalCompanies: statsRes.data.totalCompanies || 0,
        totalUsers: statsRes.data.totalUsers || 0,
        activeProjects: statsRes.data.totalProjects || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlaygroundSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/playground`);
      setPlaygroundActive(response.data.active);
    } catch (error) {
      console.error('Error loading playground settings:', error);
    }
  };

  const togglePlayground = async () => {
    setUpdatingPlayground(true);
    try {
      const newValue = !playgroundActive;
      await axios.put(`${API}/admin/settings/playground?active=${newValue}`);
      setPlaygroundActive(newValue);
      toast.success(`Playground ${newValue ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error updating playground settings:', error);
      toast.error('Failed to update playground settings');
    } finally {
      setUpdatingPlayground(false);
    }
  };

  const confirmResetPlayground = async () => {
    try {
      await axios.post(`${API}/admin/playground/reset`);
      toast.success('Playground data reset successfully');
    } catch (error) {
      toast.error('Failed to reset playground data');
    } finally {
      setShowResetPlaygroundModal(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Welcome to Admin Panel</h2>
          <p className="text-gray-600 mb-8">Manage companies and users from this dashboard</p>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-orange-500">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Companies</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {loading ? '...' : stats.totalCompanies}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-green-500">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {loading ? '...' : stats.totalUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-blue-500">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FolderKanban className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Projects</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {loading ? '...' : stats.activeProjects}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-amber-500 cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => navigate('/admin/user-settings/list?status=pending_approval')}
              data-testid="pending-approval-card"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <UserCheck className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending Approval</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {loading ? '...' : pendingApprovalCount}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => navigate('/admin/company-settings/create')}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors text-left group"
              >
                <Plus className="w-6 h-6 text-gray-400 group-hover:text-orange-600 mb-2" />
                <p className="font-medium text-gray-700 group-hover:text-orange-600">Create Company</p>
                <p className="text-sm text-gray-500">Add new company</p>
              </button>

              <button
                onClick={() => navigate('/admin/company-settings/list')}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors text-left group"
              >
                <List className="w-6 h-6 text-gray-400 group-hover:text-orange-600 mb-2" />
                <p className="font-medium text-gray-700 group-hover:text-orange-600">List Companies</p>
                <p className="text-sm text-gray-500">View all companies</p>
              </button>

              <button
                onClick={() => navigate('/admin/user-settings/create')}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-left group"
              >
                <Plus className="w-6 h-6 text-gray-400 group-hover:text-green-600 mb-2" />
                <p className="font-medium text-gray-700 group-hover:text-green-600">Create User</p>
                <p className="text-sm text-gray-500">Add new user</p>
              </button>

              <button
                onClick={() => navigate('/admin/user-settings/list')}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-left group relative"
              >
                <List className="w-6 h-6 text-gray-400 group-hover:text-green-600 mb-2" />
                <p className="font-medium text-gray-700 group-hover:text-green-600">List Users</p>
                <p className="text-sm text-gray-500">View all users</p>
                {newUsersCount > 0 && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold" data-testid="dashboard-new-users-badge">
                    {newUsersCount}
                  </span>
                )}
              </button>
            </div>

            {/* Playground Settings */}
            <div className="mt-8 bg-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Settings className="w-6 h-6 text-purple-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Playground</h3>
                    <p className="text-sm text-gray-500">
                      Public page accessible at /playground {playgroundActive && '(Active)'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowResetPlaygroundModal(true)}
                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    Reset Data
                  </button>
                  <button
                    onClick={togglePlayground}
                    disabled={updatingPlayground}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      playgroundActive ? 'bg-purple-600' : 'bg-gray-300'
                    } ${updatingPlayground ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        playgroundActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              {playgroundActive && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-700">
                    ✓ Playground is accessible at <a href="/playground" target="_blank" className="underline font-medium">/playground</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Reset Playground Confirmation Modal */}
      <ConfirmationModal
        isOpen={showResetPlaygroundModal}
        onClose={() => setShowResetPlaygroundModal(false)}
        onConfirm={confirmResetPlayground}
        type="danger"
        title="Reset Playground Data"
        message="Are you sure you want to reset all playground data? This cannot be undone."
        confirmText="Reset"
        cancelText="Cancel"
      />
    </AdminLayout>
  );
};

export default AdminDashboard;
